/**
 * PTPhotoUpload
 *
 * A thin wrapper around LHSingleImageUpload that:
 *  - Uses POST /api/perfect-truck/images/upload (containerName = "perfect-truck")
 *  - Validates JPEG/PNG and max 10 MB before sending
 *  - Reports back the uploaded fileName to the parent via onUploaded
 *  - Supports re-upload (replaces the previous file for this category)
 *
 * Usage:
 *   <PTPhotoUpload
 *     label="Forklift"
 *     onUploaded={(fileName) => setImageResponse(prev => ({ ...prev, Forklift: fileName }))}
 *   />
 */
import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Modal,
  Dimensions,
  Pressable,
  Alert,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { launchImageLibrary, launchCamera } from 'react-native-image-picker';
import Svg, { Rect } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import FontFamily from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import { uploadPerfectTruckImage } from './perfectTruckApi';
import { compressImageForUpload, UploadImage } from './imageCompression';
import { useTheme } from '../../theme';
import { useDarkStyles } from '../../theme/useDarkStyles';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

const isWeb =
  typeof (globalThis as any).document !== 'undefined' && Platform.OS === 'web';

// ── WebImage: Native HTML <img> for web to bypass React Native Web issues ──

export interface WebImageProps {
  uri: string;
  style?: any;
  resizeMode?: 'cover' | 'contain';
  onLoad?: () => void;
  onError?: (error: any) => void;
}

export const WebImage: React.FC<WebImageProps> = ({ uri, style, resizeMode = 'cover', onLoad, onError }) => {
  if (!isWeb) {
    return (
      <Image 
        source={{ uri }} 
        style={style} 
        resizeMode={resizeMode}
        onLoad={onLoad}
        onError={onError}
      />
    );
  }

  // On web, use native HTML img to handle complex Azure URLs
  return (
    <img
      src={uri}
      style={{
        ...StyleSheet.flatten(style),
        objectFit: resizeMode === 'contain' ? 'contain' : 'cover',
      } as React.CSSProperties}
      onLoad={() => onLoad?.()}
      onError={(e) => onError?.(e)}
      alt="uploaded"
    />
  );
};

// ── Props ────────────────────────────────────────────────────────────────────

interface PTPhotoUploadProps {
  /** Display label shown beneath the photo box */
  label: string;
  /** Called with {fileName (for backend storage), imageUrl (temporary display URL)} after upload */
  onUploaded: (data: { fileName: string; imageUrl: string }) => void;
  /** Optional initial preview URL (e.g. temporary SAS URL when editing an existing record) */
  initialImageUrl?: string;
  /** Aspect ratio for the box. Desktop: 16/10 (1.6). Mobile: 1. Default: 4/3 */
  aspectRatio?: number;
  /** Fixed width for the wrapper (use on desktop in a horizontal ScrollView) */
  boxWidth?: number;
  /** When true, renders as read-only: existing photo can still be previewed, but upload/re-upload is disabled */
  disabled?: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────

const PTPhotoUpload: React.FC<PTPhotoUploadProps> = ({
  label,
  onUploaded,
  initialImageUrl,
  aspectRatio,
  boxWidth,
  disabled = false,
}) => {
  const styles = useDarkStyles(baseStyles);
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // Dark-mode photo box tokens
  const emptyBoxBg = isDark ? colors.background.surface : AdminColors.blue25;
  const emptyBoxBorder = isDark ? colors.border.default : AdminColors.borderDefault;
  const emptyIconColor = isDark ? colors.text.secondary : AdminColors.gray150;
  const labelColor = isDark ? colors.text.secondary : AdminColors.textMuted;
  const labelFilledColor = isDark ? '#4ADE80' : AdminColors.green600;
  const dashedSvgColor = isDark ? colors.border.default : AdminColors.borderDefault;

  const [previewUri, setPreviewUri] = useState<string | null>(initialImageUrl ?? null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [imageLoadError, setImageLoadError] = useState<string | null>(null);
  const [boxSize, setBoxSize] = useState<{ w: number; h: number } | null>(null);
  const reuploadOverlayRef = useRef<View>(null);
  const { t } = useTranslation('perfectTruck');



  const validateFile = (type: string, size: number): string | null => {
    if (!ALLOWED_TYPES.includes(type.toLowerCase())) {
      return t('photo_upload.error_invalid_type');
    }
    if (size > MAX_FILE_SIZE) {
      return t('photo_upload.error_file_too_large');
    }
    return null;
  };

  // ── Upload ──────────────────────────────────────────────────────────────

  const doUpload = async (file: UploadImage) => {
    setUploading(true);
    setProgress(0);
    setError(null);
    setImageLoadError(null);
    setShowPreviewModal(false); // Close modal when starting upload
    try {
      const compressedFile = await compressImageForUpload(file);
      const result = await uploadPerfectTruckImage(compressedFile, setProgress);
      // result is already the 'data' field from the response
      setPreviewUri(result.imageUrl);
      
      // Verify it was set (won't see this in async, but helps debugging)
      setProgress(1);
      // Pass BOTH fileName (for backend storage) and imageUrl (for display)
      onUploaded({
        fileName: result.fileName,
        imageUrl: result.imageUrl,
      });
    } catch (e: any) {
      console.error('Perfect Truck photo upload failed:', e);
      setError(t('photo_upload.error_upload_failed'));
    } finally {
      setUploading(false);
    }
  };

  // ── Trigger (web) ───────────────────────────────────────────────────────

  const pickWeb = () => {
    const input = (globalThis as any).document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png';
    input.onchange = async (event: any) => {
      const file: File = event.target.files[0];
      if (!file) return;
      const validationError = validateFile(file.type, file.size);
      if (validationError) { setError(validationError); return; }
      await doUpload(file);
    };
    input.click();
  };

  // ── Trigger (native) ────────────────────────────────────────────────────

  const launchPicker = async (source: 'camera' | 'gallery') => {
    try {
      const options = { mediaType: 'photo' as const, quality: 1 as const };
      const result =
        source === 'camera'
          ? await launchCamera(options)
          : await launchImageLibrary(options);
      if (result.didCancel) return;
      if (result.errorMessage) {
        setError(result.errorMessage);
        return;
      }
      if (result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        const type = asset.type ?? 'image/jpeg';
        const size = asset.fileSize ?? 0;
        const validationError = validateFile(type, size);
        if (validationError) {
          setError(validationError);
          return;
        }
        await doUpload({
          uri: asset.uri!,
          name: asset.fileName ?? 'photo.jpg',
          type,
          width: asset.width,
          height: asset.height,
        });
      }
    } catch (err: any) {
      if (err?.code === 'camera_unavailable' || err?.code === 'permission') {
        Alert.alert(
          'Permission Required',
          source === 'camera'
            ? 'Camera access is required to take photos. Please enable camera permissions in Settings.'
            : 'Photo library access is required. Please enable photo permissions in Settings.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert(
          'Error',
          `Unable to ${source === 'camera' ? 'open camera' : 'access photo library'}. Please try again.`,
          [{ text: 'OK' }]
        );
      }
    }
  };

  const pickNative = () => {
    Alert.alert('Add Photo', 'How would you like to add a photo?', [
      { text: 'Take Photo', onPress: () => launchPicker('camera') },
      { text: 'Choose from Gallery', onPress: () => launchPicker('gallery') },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const handlePress = () => {
    if (uploading) return;
    if (!!previewUri && !uploading) {
      // If image exists, show preview modal (viewing is allowed even when disabled)
      setShowPreviewModal(true);
    } else if (!disabled) {
      // If no image, trigger picker (not available when disabled/read-only)
      setError(null);
      if (isWeb) pickWeb();
      else pickNative();
    }
  };

  const handleCameraPress = (e: any) => {
    e.stopPropagation();
    if (uploading || disabled) return;
    setError(null);
    if (isWeb) pickWeb();
    else pickNative();
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const hasImage = !!previewUri && !uploading;
  const windowDimensions = Dimensions.get('window');
  const modalImageSize = Math.min(windowDimensions.width * 0.9, windowDimensions.height * 0.7);

  // When parent doesn't supply `boxWidth`, limit the maximum rendered
  // dimensions so a single photo doesn't expand the box to full container width.
  // Keep different sensible limits for web/desktop vs native/mobile.
  const boxLimitStyle: any = boxWidth !== undefined ? {} : (isWeb
    ? { maxWidth: 360, maxHeight: 360 }
    : { maxWidth: 280, maxHeight: 280 }
  );


  return (
    <>
      <View style={[styles.wrapper, boxWidth !== undefined && { flex: 0, width: boxWidth, minWidth: undefined }]}>
        <TouchableOpacity
          style={[
            styles.box,
            isDark && !hasImage && !error && !disabled && { backgroundColor: emptyBoxBg, borderColor: emptyBoxBorder },
            aspectRatio !== undefined && { aspectRatio },
            // When disabled (read-only), use a muted/ash box style
            disabled && styles.boxDisabled,
            disabled && isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
            // Filled variants differ when disabled vs editable
            hasImage && !disabled && styles.boxFilled,
            hasImage && !disabled && isDark && { backgroundColor: '#1A6B24' },
            hasImage && disabled && styles.boxFilledDisabled,
            !!error && styles.boxError,
            !!error && isDark && { backgroundColor: '#9B2626' },
            // On native, suppress the RN border for empty state (rendered via SVG below)
            !isWeb && !hasImage && !error && styles.boxNoBorder,
            boxLimitStyle,
          ]}
          onPress={handlePress}
          activeOpacity={0.75}
          onLayout={(e) => {
            const { width, height } = e.nativeEvent.layout;
            if (width > 0 && height > 0 && (boxSize?.w !== width || boxSize?.h !== height)) {
              setBoxSize({ w: width, h: height });
            }
          }}
        >
          {/* Native dashed border overlay (web uses CSS borderStyle 'dashed') */}
          {!isWeb && !hasImage && !error && boxSize && (
            <Svg
              width={boxSize.w}
              height={boxSize.h}
              style={StyleSheet.absoluteFill}
              pointerEvents="none"
            >
              <Rect
                x={1}
                y={1}
                width={boxSize.w - 2}
                height={boxSize.h - 2}
                rx={10}
                ry={10}
                fill="none"
                stroke={dashedSvgColor}
                strokeWidth={2}
                strokeDasharray="6,4"
              />
            </Svg>
          )}
          {uploading ? (
            <View style={styles.uploadingState}>
              <ActivityIndicator size="small" color={isDark ? colors.brand.primary : AdminColors.blue700} />
              <Text style={[styles.progressText, isDark && { color: colors.brand.primary }]}>{Math.round(progress * 100)}%</Text>
            </View>
          ) : hasImage ? (
            <>
              <WebImage
                key={previewUri}
                uri={previewUri!}
                style={styles.preview}
                resizeMode="cover"
                onLoad={() => setImageLoadError(null)}
                onError={() => setImageLoadError(t('photo_upload.error_load_image'))}
              />
              {/* Re-upload overlay - camera icon only triggers upload, not preview. Hidden when read-only. */}
              {!disabled && (
                <TouchableOpacity
                  ref={reuploadOverlayRef}
                  style={styles.reuploadOverlay}
                  onPress={handleCameraPress}
                  activeOpacity={0.8}
                >
                  <MaterialIcons name="photo-camera" size={18} color="#fff" />
                </TouchableOpacity>
              )}
            </>
          ) : disabled ? (
            <View style={styles.emptyState}>
              <MaterialIcons name="photo-camera" size={28} color={emptyIconColor} />
            </View>
          ) : (
            <View style={styles.emptyState}>
              <MaterialIcons
                name="add-photo-alternate"
                size={28}
                color={error ? AdminColors.red400 : emptyIconColor}
              />
            </View>
          )}
        </TouchableOpacity>

        <Text
          style={[
            styles.label,
            isDark && { color: labelColor },
            hasImage && !disabled && [styles.labelFilled, isDark && { color: labelFilledColor }],
            hasImage && disabled && styles.labelFilledDisabled,
          ]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >{label}</Text>

        {error ? (
          <Text style={styles.errorText} numberOfLines={2}>{error}</Text>
        ) : null}
      </View>

      {/* Preview Modal */}
      <Modal
        visible={showPreviewModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowPreviewModal(false)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setShowPreviewModal(false)}>
          <View style={styles.modalContent}>
            {imageLoadError ? (
              <Text style={styles.modalErrorText}>{imageLoadError}</Text>
            ) : (
              <WebImage
                key={previewUri}
                uri={previewUri!}
                style={[styles.modalImage, { width: modalImageSize, height: modalImageSize }]}
                resizeMode="contain"
                onLoad={() => setImageLoadError(null)}
                onError={() => setImageLoadError(t('photo_upload.error_load_image'))}
              />
            )}
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────

const baseStyles = StyleSheet.create({
  wrapper: {
    flex: 1,
    minWidth: 80,
    alignItems: 'center',
  },
  box: {
    width: '100%',
    aspectRatio: 4 / 3,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: AdminColors.borderDefault,
    borderRadius: 10,
    backgroundColor: AdminColors.blue25,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  boxFilled: {
    borderStyle: 'solid',
    borderColor: AdminColors.green600,
    backgroundColor: AdminColors.green75,
  },
  boxError: {
    borderColor: AdminColors.red400,
    backgroundColor: AdminColors.red75,
  },
  boxNoBorder: {
    borderWidth: 0,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadingState: {
    alignItems: 'center',
    gap: 6,
  },
  progressText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: AdminColors.blue700,
  },
  preview: {
    width: '100%',
    height: '100%',
  },
  reuploadOverlay: {
    position: 'absolute',
    bottom: 6,
    right: 6,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalImage: {
    borderRadius: 8,
  },
  modalErrorText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    color: AdminColors.red400,
    textAlign: 'center',
  },
  label: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    color: AdminColors.textMuted,
    textAlign: 'center',
    marginTop: 6,
  },
  labelFilled: {
    color: AdminColors.green600,
  },
  labelFilledDisabled: {
    color: AdminColors.textMuted,
  },
  boxDisabled: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.gray25,
  },
  boxFilledDisabled: {
    borderStyle: 'solid',
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.gray25,
  },
  errorText: {
    fontFamily: FontFamily.medium,
    fontSize: 10,
    color: AdminColors.red400,
    textAlign: 'center',
    marginTop: 3,
  },
});

export default PTPhotoUpload;
